/**
 * Перенос `data/tablet_session_manager.dart`.
 *
 * `dev.log(..., name: 'TabletSession')` стал `console.debug` с тем же
 * префиксом: журнал разработчика, а не отладочный вывод.
 */

import { TabletRepositoryImpl } from '@/data/tabletRepositoryImpl';
import { configModelCopyWith, type ConfigModel } from '@/domain/models/configModel';
import { personCopyWith, type PersonModel } from '@/domain/models/personModel';
import { sttConfigCopyWith, type SttConfigModel } from '@/domain/models/sttConfigModel';
import type { TabletCharacterModel, TabletConfigModel } from '@/domain/models/tabletConfigModel';
import type { TabletRepository } from '@/domain/repository/tabletRepository';
import { DiagnosticsLog } from '@/utils/diagnostics/diagnosticsLog';
import { AppException } from '@/utils/exceptions/appException';
import { getBaseClient } from '@/utils/http/httpClient';
import { Nullable } from '@/utils/nullable';
import { ServerAddress } from '@/utils/serverAddress';
import { StorageHelper } from '@/utils/storageHelper';

/** Имя журнала разработчика — `dev.log(name: 'TabletSession')`. */
const LOG_NAME = 'TabletSession';

/** Токен протух либо отозван — цепочка идёт на refresh. */
const HTTP_UNAUTHORIZED = 401;

/** Неверный пароль (auth) или сессия больше не действительна (config). */
const HTTP_FORBIDDEN = 403;

/** Исчерпан лимит попыток входа. */
const HTTP_TOO_MANY_REQUESTS = 429;

/** Нижняя граница клиентских ошибок: с 4xx refresh больше не годится. */
const HTTP_CLIENT_ERROR_MIN = 400;

/** Нижняя граница серверных ошибок: 5xx — сервер лежит, auth не поможет. */
const HTTP_SERVER_ERROR_MIN = 500;

/** `e.statusCode ?? 0` — кода нет (сеть, формат ответа). */
const UNKNOWN_STATUS = 0;

/**
 * 4xx, после которых refresh ещё может пройти: сервер не дождался запроса
 * (408) или просит подождать (429). Сессия от них не умирает.
 */
const TEMPORARY_REFRESH_STATUSES: readonly number[] = [408, 429];

/** Чем закончилась попытка входа планшета. */
export const TabletAuthOutcome = {
  /** Токены получены и сохранены. */
  success: 'success',

  /** Сервер отверг пароль (403). */
  wrongPassword: 'wrongPassword',

  /** Исчерпан лимит попыток (429). */
  rateLimited: 'rateLimited',

  /**
   * Сервер не поддерживает вход планшета: старый бекенд (401/404/405,
   * ответ не того формата) либо сеть недоступна. Работаем без ключей.
   */
  unsupported: 'unsupported',
} as const;

export type TabletAuthOutcome = (typeof TabletAuthOutcome)[keyof typeof TabletAuthOutcome];

/** Аргументы [TabletSessionManager.authorize]. */
export interface TabletAuthorizeParams {
  readonly serverRoot: string;
  readonly slug: string;
  readonly password: string;

  /** Репозиторий от UI; без него берётся [TabletSessionManager.repositoryFactory]. */
  readonly repository?: TabletRepository | null;
}

/** Аргументы [TabletSessionManager.adoptPinTokens]. */
export interface AdoptPinTokensParams {
  readonly baseUrl: string;
  readonly token: string;
  readonly refreshToken: string;
}

/**
 * Одна цепочка config → refresh → вход по паролю. [startToken] — токен,
 * с которым она началась: стирать сессию можно, только пока в хранилище
 * лежит он, а не свежий от входа, случившегося посреди цепочки.
 */
interface SessionChain {
  readonly repo: TabletRepository;
  readonly root: string;
  readonly slug: string;
  readonly password: string | null;
  readonly startToken: string | null;
}

/** Аргументы [TabletSessionManager.refreshSttFor]. */
export interface RefreshSttForParams {
  readonly baseUrl: string;
  readonly personSlug: string;
}

/**
 * Сессия планшета: токены (по паролю с экрана адреса или по верному PIN),
 * их обновление и слияние полных STT (`api_key`) в конфигурацию.
 *
 * Синглтон, как [StorageHelper]: конфигурация загружается из статического
 * `AppStateNotifier.fetchAppState`, куда провайдеры не достают. UI передаёт
 * репозиторий сам — так тесты подменяют транспорт (в Dart это делал
 * `clientProvider` Riverpod, здесь — [repositoryFactory] и необязательный
 * `repository`).
 */
export class TabletSessionManager {
  private constructor() {}

  static instance: TabletSessionManager = new TabletSessionManager();

  /** Свежий экземпляр для тестов: чистые single-flight и фабрика репозитория. */
  static testInstance(): TabletSessionManager {
    return new TabletSessionManager();
  }

  /** Для фоновой цепочки, где провайдеров нет. Подменяется в тестах. */
  repositoryFactory: () => TabletRepository = () =>
    new TabletRepositoryImpl({ client: getBaseClient() });

  /** Не даёт параллельным загрузкам конфига дублировать auth/refresh. */
  #inflight: Promise<TabletConfigModel | null> | null = null;

  /** Адрес, для которого идёт [#inflight]: чужому адресу его ответ не отдаём. */
  #inflightUrl: string | null = null;

  #log(message: string): void {
    console.debug(`[${LOG_NAME}] ${message}`);
    DiagnosticsLog.instance.session(message);
  }

  /**
   * Вход с экрана адреса. Успех сохраняет пароль и токены для фоновой
   * цепочки; провал входа никогда не роняет подключение — максимум
   * планшет останется без `api_key`.
   */
  async authorize({
    serverRoot,
    slug,
    password,
    repository,
  }: TabletAuthorizeParams): Promise<TabletAuthOutcome> {
    const repo = repository ?? this.repositoryFactory();
    try {
      const auth = await repo.auth({ serverRoot, slug, password });
      await StorageHelper.instance.saveTabletCredentials({
        baseUrl: `${serverRoot}/${slug}`,
        password,
        token: auth.token,
        refreshToken: auth.refreshToken,
      });
      this.#log(`вход выполнен: ${serverRoot}/${slug}`);
      return TabletAuthOutcome.success;
    } catch (e) {
      if (!(e instanceof AppException)) {
        this.#log(`вход не удался: ${String(e)}`);
        return TabletAuthOutcome.unsupported;
      }
      const status = e.statusCode;
      if (status === HTTP_FORBIDDEN) return TabletAuthOutcome.wrongPassword;
      if (status === HTTP_TOO_MANY_REQUESTS) return TabletAuthOutcome.rateLimited;
      // 401/404/405 — endpoint'а нет (старый бекенд); ошибки формата и сети —
      // тоже не повод блокировать подключение.
      this.#log(`вход не поддержан или недоступен: ${e.type} status=${status}`);
      return TabletAuthOutcome.unsupported;
    }
  }

  /**
   * Токены, которые выдал верный PIN (`check-pin` нового бекенда): те же,
   * что у `tablets/auth`, пароль для ключей STT больше не обязателен.
   */
  async adoptPinTokens({ baseUrl, token, refreshToken }: AdoptPinTokensParams): Promise<void> {
    await StorageHelper.instance.saveTabletSession({ baseUrl, token, refreshToken });
    this.#log(`токены получены по PIN: ${baseUrl}`);
  }

  /**
   * Защищённая конфигурация для [baseUrl] (`root/slug`) или `null`.
   *
   * `null` — это не ошибка, а «работаем без ключей»: нет ни токенов, ни
   * пароля, старый бекенд, сеть, смена пароля на сервере. Цепочка:
   * config → 401/403 → refresh → повтор → refresh 4xx → auth по паролю (если
   * он есть) → повтор. Любой тупик — деградация без снекбаров; ключи,
   * полученные раньше, вызывающий берёт из кеша ([keepCachedStt]).
   */
  tryFetchTabletConfig(baseUrl: string): Promise<TabletConfigModel | null> {
    const inflight = this.#inflight;
    const inflightUrl = this.#inflightUrl;
    if (
      inflight !== null &&
      inflightUrl !== null &&
      StorageHelper.sameServer(inflightUrl, baseUrl)
    ) {
      return inflight;
    }
    // `whenComplete` — это `finally`: обработчик успевает отработать до того,
    // как завершится сам `future`, и значение (или ошибка) проходит насквозь.
    const future: Promise<TabletConfigModel | null> = this.#fetchTabletConfig(
      baseUrl,
    ).finally(() => {
      // Пока шёл этот запрос, мог стартовать запрос другого адреса.
      if (this.#inflight === future) {
        this.#inflight = null;
        this.#inflightUrl = null;
      }
    });
    this.#inflight = future;
    this.#inflightUrl = baseUrl;
    return future;
  }

  async #fetchTabletConfig(baseUrl: string): Promise<TabletConfigModel | null> {
    const storage = StorageHelper.instance;
    if (!storage.credentialsMatch(baseUrl)) return null;
    // Пароля может не быть: токены выдал PIN. Тогда цепочка кончается
    // на refresh — новые токены даст следующий верный PIN.
    const password = storage.tabletPassword;

    const address = ServerAddress.parse(baseUrl);
    if (!address.hasPath) return null;
    const chain: SessionChain = {
      repo: this.repositoryFactory(),
      root: address.root,
      slug: address.path,
      password,
      startToken: storage.tabletToken,
    };
    const { repo, root } = chain;

    try {
      const token = chain.startToken ?? (await this.#relogin(chain));
      if (token === null) return null;

      try {
        return await repo.fetchTabletConfig({ serverRoot: root, token });
      } catch (e) {
        if (!(e instanceof AppException)) throw e;
        if (e.statusCode !== HTTP_UNAUTHORIZED && e.statusCode !== HTTP_FORBIDDEN) throw e;
        // Токен истёк (1 час). refresh → при 403 (24 часа прошли или токен
        // отозван) → заново по паролю, если он есть.
        const fresh = await this.#refreshedToken(chain);
        if (fresh === null) return null;
        return await repo.fetchTabletConfig({ serverRoot: root, token: fresh });
      }
    } catch (e) {
      if (e instanceof AppException) {
        this.#log(`конфигурация планшета недоступна: ${e.type} status=${e.statusCode}`);
        return null;
      }
      this.#log(`конфигурация планшета недоступна: ${String(e)}`);
      return null;
    }
  }

  async #refreshedToken(chain: SessionChain): Promise<string | null> {
    const refreshToken = StorageHelper.instance.tabletRefreshToken;
    if (refreshToken !== null) {
      try {
        const tokens = await chain.repo.refresh({ serverRoot: chain.root, refreshToken });
        // refreshToken == null — бекенд не ротирует, прежний не трогаем.
        await StorageHelper.instance.saveTabletTokens({
          token: tokens.token,
          refreshToken: tokens.refreshToken,
        });
        this.#log(
          tokens.refreshToken === null
            ? 'token обновлён через refresh'
            : 'token и refresh_token обновлены через refresh',
        );
        return tokens.token;
      } catch (e) {
        if (!(e instanceof AppException)) throw e;
        // Любой 4xx — refresh больше не годится (протух, отозван, бекенд
        // отвечает на это 400/422, а не 401/403): дальше вход по паролю.
        // 5xx, сеть, 408 и 429 — сервер лежит или занят, сессия жива, и
        // долбить auth незачем.
        const status = e.statusCode ?? UNKNOWN_STATUS;
        if (status < HTTP_CLIENT_ERROR_MIN || status >= HTTP_SERVER_ERROR_MIN) throw e;
        if (TEMPORARY_REFRESH_STATUSES.includes(status)) throw e;
      }
    }
    return this.#relogin(chain);
  }

  /**
   * Повторный вход по сохранённому паролю. `null` — пароля нет (токены
   * были от PIN, а PIN хранится только хешем) или его сменили на сервере.
   * Сессия тогда мертва и стирается ([#forgetSession]): при следующем входе
   * киоск спросит пароль, новые токены даст он или верный PIN.
   */
  async #relogin(chain: SessionChain): Promise<string | null> {
    const { repo, root, slug, password } = chain;
    if (password === null) {
      await this.#forgetSession(chain, 'токены истекли, пароля нет');
      return null;
    }
    try {
      const auth = await repo.auth({ serverRoot: root, slug, password });
      await StorageHelper.instance.saveTabletTokens({
        token: auth.token,
        refreshToken: auth.refreshToken,
      });
      return auth.token;
    } catch (e) {
      if (!(e instanceof AppException)) throw e;
      if (e.statusCode === HTTP_FORBIDDEN) {
        await this.#forgetSession(chain, 'сохранённый пароль больше не подходит');
        return null;
      }
      throw e;
    }
  }

  /**
   * Сервер окончательно отверг сессию. Токены и пароль больше ничего не
   * откроют — стираем их, чтобы вход снова спросил пароль. Сеть и 5xx сюда
   * не ведут: там сессия ещё может ожить.
   *
   * Пока цепочка ждала сервер, посетитель мог войти заново: тогда в
   * хранилище уже не отвергнутый токен, а свежий, и его не трогаем.
   */
  async #forgetSession(chain: SessionChain, reason: string): Promise<void> {
    const storage = StorageHelper.instance;
    if (storage.tabletToken !== chain.startToken) {
      this.#log(`${reason}, но сессию уже обновили — не трогаем`);
      return;
    }
    await storage.clearTabletCredentials();
    this.#log(`${reason} — сессия стёрта, при входе спросим пароль`);
  }

  /**
   * Вливает полные STT из защищённой конфигурации в открытую.
   *
   * Матчинг только по `slug` — `id` у списков разные (int и UUID).
   * Персона без пары в `characters[]` не трогается: админ мог поменять
   * состав между двумя запросами, это не ошибка.
   */
  static mergeStt(config: ConfigModel, tablet: TabletConfigModel): ConfigModel {
    const bySlug = new Map<string, TabletCharacterModel>(
      tablet.characters.map((character) => [character.slug, character]),
    );
    return configModelCopyWith(config, {
      persons: config.persons.map((person) => {
        const character = bySlug.get(person.slug);
        if (character === undefined) return person;
        return personCopyWith(person, {
          characterType: character.characterType,
          stt: new Nullable(mergePersonStt(person.stt, character.stt)),
        });
      }),
    });
  }

  /**
   * Защищённая конфигурация недоступна (сессия истекла, сеть): переносит
   * в свежую открытую конфигурацию то, что раньше пришло из защищённой, —
   * ключ STT и `characterType`. Без этого открытый ответ без `api_key`
   * затирал бы кеш, и голос отваливался с 401 до следующего PIN.
   *
   * Матчинг по `slug`, как в [mergeStt]. URL и модель — из свежего ответа:
   * их админ мог сменить, а открытый ответ их отдаёт. `stt: null` в свежем
   * ответе — голос у аватара выключен, ключ не нужен.
   */
  static keepCachedStt(fresh: ConfigModel, cached: ConfigModel | null): ConfigModel {
    if (cached === null) return fresh;
    const bySlug = new Map<string, PersonModel>(
      cached.persons.map((person) => [person.slug, person]),
    );
    return configModelCopyWith(fresh, {
      persons: fresh.persons.map((person) => {
        const old = bySlug.get(person.slug);
        if (old === undefined) return person;
        return personCopyWith(person, {
          characterType: old.characterType,
          stt: new Nullable(keepCachedPersonStt(person.stt, old.stt)),
        });
      }),
    });
  }

  /**
   * Свежий STT персоны после отказа STT-сервиса (401/403): админ мог
   * сменить ключ. Перечитывает защищённую конфигурацию, вливает её
   * в сохранённую и возвращает STT по [personSlug].
   */
  async refreshSttFor({
    baseUrl,
    personSlug,
  }: RefreshSttForParams): Promise<SttConfigModel | null> {
    const tablet = await this.tryFetchTabletConfig(baseUrl);
    if (tablet === null) return null;
    const storage = StorageHelper.instance;
    const config = storage.config;
    if (config === null) return null;
    const merged = TabletSessionManager.mergeStt(config, tablet);
    await storage.saveConfig(merged);
    for (const person of merged.persons) {
      if (person.slug === personSlug) return person.stt;
    }
    return null;
  }
}

/**
 * `_mergePersonStt`. Параметры переименованы (`public` — зарезервированное
 * слово в строгом режиме JS), смысл прежний: открытый и защищённый STT.
 */
function mergePersonStt(
  publicStt: SttConfigModel | null,
  protectedStt: SttConfigModel | null,
): SttConfigModel | null {
  if (protectedStt === null) return publicStt;
  // Край 2.5: у персонажа стёрты URL и модель, но остался ключ —
  // защищённый ответ отдаёт {api_url: null, api_key: "…"}. URL тогда
  // берётся из открытого ответа; нет и там — голос выключен превентивно.
  return {
    apiUrl: nonEmpty(protectedStt.apiUrl) ?? publicStt?.apiUrl ?? null,
    model: protectedStt.model ?? publicStt?.model ?? null,
    apiKey: nonEmpty(protectedStt.apiKey),
  };
}

/** `_keepCachedPersonStt`. */
function keepCachedPersonStt(
  fresh: SttConfigModel | null,
  cached: SttConfigModel | null,
): SttConfigModel | null {
  if (fresh === null) return null;
  return sttConfigCopyWith(fresh, {
    apiUrl: new Nullable(nonEmpty(fresh.apiUrl) ?? cached?.apiUrl ?? null),
    model: new Nullable(fresh.model ?? cached?.model ?? null),
    apiKey: new Nullable(nonEmpty(cached?.apiKey)),
  });
}

/** `_nonEmpty`: `null`, пустая строка и одни пробелы — это «нет значения». */
function nonEmpty(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? null;
  return trimmed === null || trimmed.length === 0 ? null : trimmed;
}
